import React from 'react'

import { PaneArea } from '../../../types/shared'
import { Toolbar as ToolbarComponent, ToolbarDivider, ToolbarItem } from '../../components'
import { SpecificChartFunctionality } from '../../constants/common'
import { drawingToolsCreatorsByType, drawingToolsTouchCreatorsByType } from '../../creators/index'
import { ElementStyleDialogContext } from '../../modals/element_style'
import PaneModel from '../../models/pane'
import { getIsAutoSaveEnabled } from '../../utils/chart'
import { toolbarDrawingActions } from './drawingActions'
import { toolbarDrawingSettings } from './drawingSettings'
import { drawingTools, toolbarDrawingTools } from './drawingTools'
import { DrawingSetting, DrawingTool, IToolBarProps, IToolbarDrawingsSettingItem } from './interfaces'

const DISABLED_ITEM_TOOLTIP_LABEL = "Disabled due 'lock/hide' drawings active"

export default function Toolbar({
  isSidebar,
  isHorizontal,
  activeTool,
  specificChartFunctionality,
  onActiveToolChange,
  onDrawingActionClick,
  onDrawingSettingClick,
  drawingSettings,
  isPreserveDrawingsAvailable,
  isAlternativeTheme,
  areToolsDisabled,
  isIdea,
}: IToolBarProps) {
  const [isDrawing, setIsDrawing] = React.useState(false)
  const openElementStyleDialog = React.useContext(ElementStyleDialogContext)

  const availableDrawingSettings = toolbarDrawingSettings.reduce((acc, item) => {
    if (item.defaultItem.id === DrawingSetting.PreserveDrawings) {
      const hasPersistDrawingButton =
        isPreserveDrawingsAvailable &&
        [SpecificChartFunctionality.chartPage, SpecificChartFunctionality.quotePage].includes(
          specificChartFunctionality
        )

      if (!hasPersistDrawingButton) {
        return acc
      }

      return [
        ...acc,
        {
          ...item,
          isDisabled: isIdea && getIsAutoSaveEnabled(),
          disabledTooltip: 'Persist drawings is not available in ideas',
        },
      ]
    }

    return [...acc, item]
  }, [] as IToolbarDrawingsSettingItem[])

  React.useEffect(() => {
    const onMouseDown = (model: PaneModel, area: PaneArea & { isTouch: boolean }) => {
      const isDrawingModeContinuous = drawingSettings[DrawingSetting.DrawingModeContinuous]
      const hasData = model.chart().quote().close.length > 0
      const hasHiddenDrawings = drawingSettings[DrawingSetting.HideDrawings]
      const tool = drawingTools[activeTool].id

      if (tool === DrawingTool.Mouse || isDrawing || hasHiddenDrawings || !hasData) {
        return
      }

      setIsDrawing(true)
      const ToolClass = area.isTouch ? drawingToolsTouchCreatorsByType[tool] : drawingToolsCreatorsByType[tool]
      const element = model.elements().create({
        instance: new ToolClass(area, {}, model, openElementStyleDialog, () => {
          if (!isDrawingModeContinuous) {
            onActiveToolChange(DrawingTool.Mouse)
          }
          setIsDrawing(false)
        }),
      })
      element.one('destroy', ({ instance }) => {
        if (instance.getIsCreator()) {
          setIsDrawing(false)
        }
      })

      element.trigger('select', element)
    }
    PaneModel.bind('mousedown', onMouseDown)
    return () => {
      PaneModel.unbind('mousedown', onMouseDown)
    }
  }, [activeTool, onActiveToolChange, openElementStyleDialog, drawingSettings, isDrawing])

  return (
    <ToolbarComponent
      direction={isHorizontal && !isSidebar ? 'horizontal' : 'vertical'}
      theme={isAlternativeTheme ? 'alternative' : undefined}
    >
      {toolbarDrawingTools.map((item) => (
        <ToolbarItem
          key={item.defaultItem.id}
          defaultItem={item.defaultItem}
          isActive={activeTool === item.defaultItem.id || item.toolGroup?.some((tool) => tool.id === activeTool)}
          groupItems={item.toolGroup}
          groupTitle={item.toolGroupTitle}
          onChange={onActiveToolChange}
          disabled={item.defaultItem.id !== DrawingTool.Mouse && areToolsDisabled}
          disabledTooltip={DISABLED_ITEM_TOOLTIP_LABEL}
        />
      ))}
      <ToolbarDivider />
      {availableDrawingSettings.map((item) => (
        <ToolbarItem
          key={item.defaultItem.id}
          defaultItem={item.defaultItem}
          isActive={drawingSettings[item.defaultItem.id]}
          groupItems={item.toolGroup}
          groupTitle={item.toolGroupTitle}
          disabledTooltip={item.disabledTooltip}
          disabled={item.isDisabled}
          onChange={onDrawingSettingClick}
        />
      ))}
      {toolbarDrawingActions.map((item) => (
        <ToolbarItem
          key={item.defaultItem.id}
          defaultItem={item.defaultItem}
          isActive={false}
          groupItems={item.toolGroup}
          groupTitle={item.toolGroupTitle}
          onChange={onDrawingActionClick}
        />
      ))}
    </ToolbarComponent>
  )
}
